import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  CssBaseline,
  Paper,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { publicApi } from '../../api/resultsApi';
import { useCompetitionLive } from '../../hooks/useCompetitionLive';
import { BoardHeader } from '../../components/board/BoardShared';
import { MatrixBoard } from '../../components/board/MatrixBoard';
import { FocusBoard } from '../../components/board/FocusBoard';
import { CarouselBoard } from '../../components/board/CarouselBoard';
import { SplitBoard } from '../../components/board/SplitBoard';
import { ColumnsBoard } from '../../components/board/ColumnsBoard';
import { ScreensaverBoard } from '../../components/board/ScreensaverBoard';
import { theme as lightTheme } from '../../theme';
import type { BoardLayout, PublicThemeMode } from '../../types/domain';
import { useLayoutVisibility } from '../../hooks/useLayoutVisibility';

const BOARD_LAYOUT_VALUES: readonly BoardLayout[] = ['MATRIX', 'FOCUS', 'CAROUSEL', 'SPLIT', 'COLUMNS'];
const normalizeBoardLayout = (value: unknown, fallback: BoardLayout): BoardLayout =>
  BOARD_LAYOUT_VALUES.includes(value as BoardLayout) ? (value as BoardLayout) : fallback;

/** Urci zda je aktualne "den" (6-20 h) — pro AUTO rezim. */
const isDaytime = (d: Date): boolean => {
  const h = d.getHours();
  return h >= 6 && h < 20;
};

/** Resi effective theme mode podle nastaveni + casu / systemu. */
const resolveEffectiveMode = (setting: PublicThemeMode): 'light' | 'dark' => {
  if (setting === 'LIGHT') return 'light';
  if (setting === 'DARK') return 'dark';
  // AUTO: nejprve system preference, pak cas jako fallback (na TV bez system prefu)
  if (typeof window !== 'undefined' && window.matchMedia) {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) return 'dark';
  }
  return isDaytime(new Date()) ? 'light' : 'dark';
};

const makeBoardTheme = (mode: 'light' | 'dark') =>
  createTheme({
    ...lightTheme,
    palette: {
      ...lightTheme.palette,
      mode,
      ...(mode === 'dark'
        ? {
            background: {
              default: '#0F0F10',
              paper: '#1C1C1F',
            },
            text: {
              primary: '#FFFFFF',
              secondary: '#B0BEC5',
            },
            divider: '#2E2E33',
          }
        : {
            // Light = jako veřejná stránka, ale vyšší kontrast
            background: {
              default: '#FAFAFA',
              paper: '#FFFFFF',
            },
            text: {
              primary: '#0A0A0A',
              secondary: '#424242',
            },
          }),
    },
  });

export const BoardPage = () => {
  const { token } = useParams<{ token: string }>();

  const { data: lookup, isLoading: lookupLoading, error: lookupError } = useQuery({
    queryKey: ['board', token],
    queryFn: () => publicApi.boardLookup(token!),
    enabled: !!token,
    retry: false,
  });
  const shortCode = lookup?.shortCode ?? null;

  useCompetitionLive(shortCode ?? undefined);
  const { data: visibility } = useLayoutVisibility();

  const { data: competition } = useQuery({
    queryKey: ['public', shortCode, 'detail'],
    queryFn: () => publicApi.competitionDetail(shortCode!),
    enabled: !!shortCode,
    // Fallback polling pro pripad ztraty WS spojeni — drz se na tempu zbytku boardu.
    refetchInterval: (query) => (query.state.data?.publicAutoRefreshSeconds ?? 15) * 1000,
  });

  const refreshSec = competition?.publicAutoRefreshSeconds ?? 15;
  const { data: results = [] } = useQuery({
    queryKey: ['public', shortCode, 'results'],
    queryFn: () => publicApi.results(shortCode!),
    enabled: !!shortCode,
    refetchInterval: refreshSec * 1000,
  });

  const overallEnabled =
    !!shortCode &&
    !!competition?.overallRankingVisible &&
    (competition?.boardLayout === 'COLUMNS' || !!competition?.publicRotateOverall);
  const { data: overall = [] } = useQuery({
    queryKey: ['public', shortCode, 'overall'],
    queryFn: () => publicApi.overall(shortCode!),
    enabled: overallEnabled,
    refetchInterval: refreshSec * 1000,
  });

  // Seznam vsech registrovanych tymu — nacitame vzdy, aby se nove pridane druzstvo
  // objevilo na platu okamzite i u layoutu rizenych vysledky (MATRIX/FOCUS/CAROUSEL/SPLIT).
  const { data: allTeams } = useQuery({
    queryKey: ['public', shortCode, 'teams'],
    queryFn: () => publicApi.teams(shortCode!),
    enabled: !!shortCode,
    refetchInterval: refreshSec * 1000,
  });

  // Effective theme mode — prepocitava se minutove pro AUTO rezim
  const [themeTick, setThemeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setThemeTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const effectiveMode = useMemo(() => {
    const setting = competition?.boardThemeMode ?? 'AUTO';
    return resolveEffectiveMode(setting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition?.boardThemeMode, themeTick]);

  const boardTheme = useMemo(() => makeBoardTheme(effectiveMode), [effectiveMode]);

  // Skalovani pisma POUZE tabulek s vysledky (ne hlavicky).
  // Aplikovano pres CSS `zoom` na wrapper kolem layoutu.
  const fontScalePct = competition?.boardFontScale ?? 100;

  const publicUrl = useMemo(() => {
    if (!shortCode) return '';
    return `${window.location.origin}/s/${shortCode}`;
  }, [shortCode]);

  if (lookupError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Neplatný odkaz
          </Typography>
          <Typography color="text.secondary">
            Tento odkaz na plat buď vypršel, nebo je neplatný. Zkontrolujte URL nebo kontaktujte
            pořadatele soutěže.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (lookupLoading || !competition || !shortCode) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const showOrganizerLogo =
    competition.publicShowOrganizerLogo &&
    (competition.publicOrganizerLogoMime ||
      (competition.publicOrganizerLogoUrl && /^https?:\/\//i.test(competition.publicOrganizerLogoUrl)));
  const organizerLogoSrc = showOrganizerLogo
    ? competition.publicOrganizerLogoMime
      ? `/api/public/competitions/${shortCode}/organizer-logo`
      : competition.publicOrganizerLogoUrl
    : null;

  const boardDefault = visibility?.boardDefault ?? 'MATRIX';
  const rawLayout = normalizeBoardLayout(competition.boardLayout, boardDefault);
  const boardHidden = visibility ? visibility.boardLayouts[rawLayout] === false : false;
  const layout: BoardLayout = boardHidden ? boardDefault : rawLayout;

  // Matrix má velký QR sidebar; u ostatních layoutů dáme malý QR do hlavičky vedle názvu.
  const inlineQrShortCode = layout !== 'MATRIX' ? shortCode : undefined;

  // Sporic pred zahajenim souteze — misto prazdneho platu ukaz identitu + QR + info.
  const screensaverActive =
    competition.status === 'DRAFT' && competition.boardScreensaverEnabled;

  return (
    <ThemeProvider theme={boardTheme}>
      <CssBaseline />
      <Helmet>
        <title>{competition.name} — plat | HasičiOnline.cz</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          p: { xs: 1.5, md: 1 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {screensaverActive ? (
          <ScreensaverBoard
            competition={competition}
            shortCode={shortCode}
            organizerLogoSrc={organizerLogoSrc}
          />
        ) : (
        <>
        <BoardHeader
          competition={competition}
          organizerLogoSrc={organizerLogoSrc}
          inlineQrShortCode={inlineQrShortCode}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            // CSS zoom skaluje obsah (fonty + padding + layout) jen uvnitr wrapperu.
            // Hlavicka (BoardHeader) je mimo wrapper, takze zustane ve vychozi velikosti.
            zoom: fontScalePct / 100,
          }}
        >
          {layout === 'MATRIX' && (
            <MatrixBoard
              competition={competition}
              results={results}
              overall={overall}
              allTeams={allTeams}
              shortCode={shortCode}
              publicUrl={publicUrl}
            />
          )}
          {layout === 'FOCUS' && (
            <FocusBoard competition={competition} results={results} overall={overall} allTeams={allTeams} />
          )}
          {layout === 'CAROUSEL' && (
            <CarouselBoard competition={competition} results={results} overall={overall} allTeams={allTeams} />
          )}
          {layout === 'SPLIT' && (
            <SplitBoard competition={competition} results={results} overall={overall} allTeams={allTeams} />
          )}
          {layout === 'COLUMNS' && (
            <ColumnsBoard competition={competition} results={results} overall={overall} allTeams={allTeams} />
          )}
        </Box>
        </>
        )}
      </Box>
    </ThemeProvider>
  );
};
